import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import type { DataTablePaginationProps } from '../types';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { CLIENT_PAGE_TABLES_PAGE_SIZES } from '@/constants/pagination';

export const DataTablePagination = (props: DataTablePaginationProps) => {
  const {
    page,
    sizePerPage,
    recordsTotal,
    onPageChange,
    onSizePerPageChange,
    showInfo = true,
    pageSizes = CLIENT_PAGE_TABLES_PAGE_SIZES,
  } = props;
  const { t } = useTranslation();

  const start = (page - 1) * sizePerPage + 1;
  const end = Math.min(page * sizePerPage, recordsTotal);
  const hasNext = page * sizePerPage < recordsTotal;
  const hasPrev = page > 1;

  const onSizePerPageChangeInternal = (value: string) => {
    const newSize = parseInt(value, 10);
    if (onSizePerPageChange) {
      onSizePerPageChange(newSize);
    }
  };

  return (
    <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-end md:gap-4">
      {/* Mobile: Navigation buttons on first row */}
      <div className="flex w-full gap-2 md:hidden">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPrev}
          className="h-10 flex-1"
        >
          <ChevronLeft className="mr-2 size-4" />
          {t('Previous')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNext}
          className="h-10 flex-1"
        >
          {t('Next')}
          <ChevronRight className="ml-2 size-4" />
        </Button>
      </div>

      {/* Mobile: Info on second row */}
      <div className="flex flex-col gap-2 md:hidden">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-xs">
            {t('Rows Per Page')}
          </span>
          <Select
            value={sizePerPage.toString()}
            onValueChange={onSizePerPageChangeInternal}
          >
            <SelectTrigger className="h-9 w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizes.map((size) => (
                <SelectItem
                  key={size}
                  value={size.toString()}
                >
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {showInfo && (
            <div className="text-muted-foreground ml-auto text-xs">
              {start} - {end} {t('Of')} {recordsTotal}
            </div>
          )}
        </div>
      </div>

      {/* Desktop: All in one row */}
      <div className="hidden items-center gap-2 md:flex">
        <span className="text-muted-foreground text-sm">
          {t('Rows Per Page')}
        </span>
        <Select
          value={sizePerPage.toString()}
          onValueChange={onSizePerPageChangeInternal}
        >
          <SelectTrigger className="w-25">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pageSizes.map((size) => (
              <SelectItem
                key={size}
                value={size.toString()}
              >
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {showInfo && (
        <div className="text-muted-foreground hidden text-sm md:block">
          {start} - {end} {t('Of')} {recordsTotal}
        </div>
      )}
      <div className="hidden items-center gap-2 md:flex">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPrev}
          className="size-8 p-0"
        >
          <ChevronLeft className="size-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNext}
          className="size-8 p-0"
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
};
